/**
 * Hierarchical layout for protocol DAG nodes using dagre.
 */

import dagre from "@dagrejs/dagre";
import type { Edge, Node } from "@xyflow/react";
import type { ProtocolDagNodeData } from "./protocolDagModel";

const NODE_WIDTH = 220;
const NODE_HEIGHT = 88;

/**
 * Assign dagre positions to protocol graph nodes.
 *
 * @param nodes - Unpositioned React Flow nodes
 * @param edges - React Flow edges
 * @returns Nodes with updated positions
 */
export function layoutProtocolGraph(
  nodes: Node<ProtocolDagNodeData>[],
  edges: Edge[],
): Node<ProtocolDagNodeData>[] {
  if (nodes.length === 0) return nodes;

  const graph = new dagre.graphlib.Graph();
  graph.setDefaultEdgeLabel(() => ({}));
  graph.setGraph({
    rankdir: "LR",
    nodesep: 36,
    ranksep: 72,
    marginx: 24,
    marginy: 24,
  });

  for (const node of nodes) {
    const height = node.data.kind === "atom" ? 104 : NODE_HEIGHT;
    graph.setNode(node.id, { width: NODE_WIDTH, height });
  }
  for (const edge of edges) {
    graph.setEdge(edge.source, edge.target);
  }

  dagre.layout(graph);

  return nodes.map((node) => {
    const positioned = graph.node(node.id);
    const height = node.data.kind === "atom" ? 104 : NODE_HEIGHT;
    return {
      ...node,
      position: {
        x: positioned.x - NODE_WIDTH / 2,
        y: positioned.y - height / 2,
      },
    };
  });
}
